import { useTranslation } from 'react-i18next'
import { Bookmark, ChevronDown, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { SavedQuery } from '../../hooks/useSavedQueries'

interface SavedQueriesMenuProps {
  saved: SavedQuery[]
  loading?: boolean
  onLoad: (query: SavedQuery) => void
  onDelete: (query: SavedQuery) => void
}

/** The user's saved quick reports, one click from being loaded and run again. */
export function SavedQueriesMenu({ saved, loading, onLoad, onDelete }: SavedQueriesMenuProps) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={loading}>
          <Bookmark className="size-4" />
          {t('queries.saved.title')}
          {saved.length > 0 && <span className="text-muted-foreground">{saved.length}</span>}
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>{t('queries.saved.title')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {saved.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">{t('queries.saved.empty')}</p>
        ) : (
          saved.map((query) => (
            <DropdownMenuItem key={query.id} onSelect={() => onLoad(query)} className="group">
              <span className="min-w-0 flex-1 truncate">{query.name}</span>
              <Button
                variant="ghost"
                size="icon-sm"
                className="-my-1 opacity-0 group-hover:opacity-100 group-focus:opacity-100"
                aria-label={t('queries.saved.delete', { name: query.name })}
                // Deleting is not loading: keep the click on the button.
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onDelete(query)
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
